import { inboxWidgetCss } from "./inboxWidgetCss";

export type InboxFormatBody = (raw: string, subject?: string) => string;
export type InboxFormatAppEmoji = (raw: string, imageUrl?: string) => string;

export type MsgAutoInboxMountOptions = {
    apiBase: string;
    getToken?: (interactive?: boolean) => Promise<string> | string;
    /** Web SSO cookie against /Inbox. Default is bearer token against /v2.0/Notifications. */
    authMode?: "bearer" | "cookie";
    offcanvas?: boolean;
    fillViewport?: boolean;
    pageSize?: number;
    slackPreview?: boolean;
    formatBody?: InboxFormatBody;
    formatAppEmoji?: InboxFormatAppEmoji;
    /** Wait until the panel opens before fetching so MSAL popup stays in the click gesture. */
    lazy?: boolean;
    /** Host page already shows a Messages heading (GMOne card). */
    hideHeader?: boolean;
    /** Try silent token first; if Entra needs a popup, show a Sign in button in the card. */
    authPrompt?: boolean;
    /** Show Previous/Next under the widget list. */
    pager?: boolean;
    /** Re-fetch the first page on this interval (ms). 0 disables. */
    pollMs?: number;
};

export type MsgAutoInboxHandle = { reload: () => void };

const STYLE_ID = "msgauto-inbox-style";
const OVERLAY_ID = "msgauto-inbox-overlay";
const OVERLAY_CLOSE_SVG =
    `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">` +
    `<path d="M7.93167 6.43835L12.8711 1.49893L11.8105 0.438354L6.87109 5.37778L1.93182 0.438354L0.871094 1.49893L5.81052 6.43835L0.871094 11.3778L1.93182 12.4384L6.87109 7.49893L11.8105 12.4384L12.8711 11.3778L7.93167 6.43835Z" fill="#181E28"></path>` +
    `</svg>`;

const EMOJI: Record<string, string> = {
    exclamation: "❗",
    warning: "⚠️",
    white_check_mark: "✅",
    x: "❌",
    heavy_check_mark: "✔️",
    information_source: "ℹ️",
    question: "❓",
    bangbang: "‼️",
    iphone: "📱",
    phone: "☎️",
    email: "📧",
    envelope: "✉️",
    bell: "🔔",
    bell_ringer: "🛎️",
    no_bell: "🔕",
    cookie: "🍪"
};

type RenderState = {
    status?: string;
    error?: boolean;
    meta?: string;
    items?: Record<string, unknown>[];
    page?: number;
    totalPages?: number;
    hasPrevious?: boolean;
    hasNext?: boolean;
    offcanvas?: boolean;
    fillViewport?: boolean;
    slackPreview?: boolean;
    hideHeader?: boolean;
    signIn?: boolean;
    pager?: boolean;
    pageSize?: number;
    formatBody?: InboxFormatBody;
    formatAppEmoji?: InboxFormatAppEmoji;
};

function escapeHtml(value: unknown): string {
    return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function escapeAttr(value: unknown): string {
    return escapeHtml(value).replace(/'/g, "&#39;");
}

export function injectInboxStyles(): void {
    if (document.getElementById(STYLE_ID)) {
        return;
    }
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = inboxWidgetCss;
    document.head.appendChild(style);
}

function resolveRoot(target: string | Element): Element | null {
    if (typeof target === "string") {
        return document.querySelector(target);
    }
    return target;
}

function formatWhen(iso: unknown): string {
    if (!iso) {
        return "";
    }
    const date = new Date(String(iso));
    if (isNaN(date.getTime())) {
        return "";
    }
    const now = new Date();
    const sameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();
    if (sameDay) {
        return date.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    return date.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
}

function replaceEmoji(text: string): string {
    return text.replace(/:([a-zA-Z0-9_+\-]+):/g, (_, name: string) =>
        Object.prototype.hasOwnProperty.call(EMOJI, name) ? EMOJI[name] : ""
    );
}

function formatInline(text: string): string {
    let s = escapeHtml(replaceEmoji(text));
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    s = s.replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?:;]|$)/g, "$1<em>$2</em>");
    s = s.replace(/~([^~\n]+)~/g, "<del>$1</del>");
    s = s.replace(/ \| /g, "<span class=\"msgauto-inbox__sep\">·</span>");
    return s;
}

function preparePlain(text: string): string {
    return String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\s+(\d+)\.\s+/g, "\n$1. ")
        .replace(/\s+•\s+/g, "\n• ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

function formatMrkdwn(raw: string): string {
    const src = preparePlain(raw);
    if (!src) {
        return "";
    }

    let html = "";
    let last = 0;
    const linkRe = /<((?:https?:\/\/|mailto:|tel:)[^|>]+)(?:\|([^>]+))?>/g;
    let match: RegExpExecArray | null;
    while ((match = linkRe.exec(src))) {
        html += formatInline(src.slice(last, match.index)).replace(/\n/g, "<br>");
        const href = match[1];
        const label = match[2] != null && match[2] !== "" ? match[2] : match[1];
        html +=
            `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${formatInline(label)}</a>`;
        last = match.index + match[0].length;
    }
    html += formatInline(src.slice(last)).replace(/\n/g, "<br>");
    return html;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function isBlockKitJson(value: string): boolean {
    const trimmed = value.trim();
    return trimmed.startsWith("[") || trimmed.startsWith("{");
}

function looksLikeOpaqueId(value: string): boolean {
    const trimmed = value.trim();
    return (
        /^[a-f0-9]{32}$/i.test(trimmed) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
    );
}

function applyTextStyle(text: string, style: unknown): string {
    if (text.includes("\n")) {
        return text.split("\n").map((part) => applyTextStyle(part, style)).join("\n");
    }
    const leading = text.match(/^\s*/)?.[0] ?? "";
    const trailing = text.match(/\s*$/)?.[0] ?? "";
    const core = text.slice(leading.length, text.length - trailing.length);
    if (!core) {
        return "";
    }
    if (!isRecord(style)) {
        return text;
    }
    let out = core;
    if (style.bold) {
        out = `*${out}*`;
    }
    if (style.italic) {
        out = `_${out}_`;
    }
    if (style.strike) {
        out = `~${out}~`;
    }
    if (style.code) {
        out = `\`${out}\``;
    }
    return `${leading}${out}${trailing}`;
}

function richElementToMrkdwn(el: unknown): string {
    if (!isRecord(el)) {
        return "";
    }
    const type = String(el.type || "");
    if (type === "emoji") {
        const name = String(el.name || "").trim();
        return name ? `:${name}:` : "";
    }
    if (type === "link") {
        const url = String(el.url || "");
        const label = String(el.text || el.url || "");
        return url ? `<${url}|${label}>` : label;
    }
    const text = typeof el.text === "string" ? el.text : "";
    if (!text) {
        return "";
    }
    const meaningful = text.replace(/[\u200b\u200c\u200d\ufeff]/g, "");
    if (!meaningful.trim() && !text.includes("\n")) {
        return "";
    }
    return applyTextStyle(text, el.style);
}

function richSectionToMrkdwn(section: unknown): string {
    if (!isRecord(section)) {
        return "";
    }
    const elements = Array.isArray(section.elements) ? section.elements : [];
    return elements.map(richElementToMrkdwn).join("");
}

function richListToMrkdwn(list: unknown): string {
    if (!isRecord(list)) {
        return "";
    }
    const indent = Number(list.indent) || 0;
    const indentPad = "    ".repeat(Math.max(0, indent));
    const items = Array.isArray(list.elements) ? list.elements : [];
    return items
        .map((item) => {
            const content = richSectionToMrkdwn(item);
            if (!content.trim()) {
                return "";
            }
            let started = false;
            return content
                .split("\n")
                .map((line) => {
                    if (!line.trim()) {
                        return "";
                    }
                    if (!started) {
                        started = true;
                        return `${indentPad}• ${line.trim()}`;
                    }
                    return `${indentPad}${line.trim()}`;
                })
                .join("\n");
        })
        .filter(Boolean)
        .join("\n");
}

function richTextBlockToMrkdwn(block: unknown): string {
    if (!isRecord(block)) {
        return "";
    }
    const elements = Array.isArray(block.elements) ? block.elements : [];
    const parts: string[] = [];
    for (const el of elements) {
        if (!isRecord(el)) {
            continue;
        }
        const type = String(el.type || "");
        if (type === "rich_text_list") {
            parts.push(richListToMrkdwn(el));
        } else if (type === "rich_text_preformatted") {
            parts.push(`\`\`\`\n${richSectionToMrkdwn(el)}\n\`\`\``);
        } else {
            parts.push(richSectionToMrkdwn(el));
        }
    }
    return parts.filter((part) => part.length > 0).join("\n");
}

function textObjectToMrkdwn(text: unknown): string {
    if (typeof text === "string") {
        return text;
    }
    if (!isRecord(text)) {
        return "";
    }
    return String(text.text || "");
}

function contextToMrkdwn(block: unknown): string {
    if (!isRecord(block)) {
        return "";
    }
    const elements = Array.isArray(block.elements) ? block.elements : [];
    return elements
        .map((el) => {
            if (!isRecord(el)) {
                return "";
            }
            if (el.type === "emoji") {
                const name = String(el.name || "").trim();
                return name ? `:${name}:` : "";
            }
            return textObjectToMrkdwn(el);
        })
        .filter(Boolean)
        .join(" ");
}

function containerToMrkdwn(block: unknown): string {
    if (!isRecord(block)) {
        return "";
    }
    const title = richTextBlockToMrkdwn(block.rich_text_title);
    const children = Array.isArray(block.child_blocks) ? block.child_blocks : [];
    const childMrkdwn = children.map(blockToMrkdwn).filter(Boolean).join("\n\n");
    return [title, childMrkdwn].filter(Boolean).join("\n\n");
}

function blockToMrkdwn(block: unknown): string {
    if (!isRecord(block)) {
        return "";
    }
    const type = String(block.type || "");
    if (type === "container") {
        return containerToMrkdwn(block);
    }
    if (type === "rich_text") {
        return richTextBlockToMrkdwn(block);
    }
    if (type === "section") {
        return textObjectToMrkdwn(block.text);
    }
    if (type === "context") {
        return contextToMrkdwn(block);
    }
    if (type === "divider") {
        return "─".repeat(40);
    }
    if (block.rich_text_title || block.child_blocks) {
        return containerToMrkdwn(block);
    }
    return "";
}

function blockKitToMrkdwn(raw: string): string | null {
    if (!isBlockKitJson(raw)) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        const blocks = Array.isArray(parsed) ? parsed : [parsed];
        const parts: string[] = [];
        let prevWasContainer = false;
        for (const block of blocks) {
            const type = isRecord(block) ? String(block.type || "") : "";
            const mrkdwn = blockToMrkdwn(block);
            if (!mrkdwn) {
                continue;
            }
            if (prevWasContainer && type === "context") {
                parts.push("\u2500".repeat(40));
            }
            parts.push(mrkdwn);
            prevWasContainer = type === "container";
        }
        return parts.join("\n\n").trim() || null;
    } catch {
        return null;
    }
}

function rowBody(item: Record<string, unknown>): string {
    const summary = String(item.summary || item.Summary || "").trim();
    if (summary) {
        return summary;
    }
    const raw = String(
        item.bodyRaw || item.BodyRaw || item.slackMessage || item.SlackMessage || ""
    ).trim();
    if (!raw) {
        return "";
    }
    if (!isBlockKitJson(raw)) {
        return raw;
    }
    return blockKitToMrkdwn(raw) || "";
}

function rowSubject(item: Record<string, unknown>): string {
    const subject = String(item.subject || item.Subject || "").trim();
    const messageId = String(item.messageId || item.MessageId || "").trim();
    if (!subject || subject === messageId || looksLikeOpaqueId(subject)) {
        return "";
    }
    return subject;
}

function collectRows(notification: Record<string, unknown>): Array<{
    app: string;
    emoji: string;
    emojiImageUrl: string;
    template: string;
    line: string;
}> {
    const rows = [];
    const apps = (notification.applications || notification.Applications || []) as Record<string, unknown>[];
    for (const app of apps) {
        const templates = (app.templates || app.Templates || []) as Record<string, unknown>[];
        for (const tmpl of templates) {
            const items = (tmpl.items || tmpl.Items || []) as Record<string, unknown>[];
            for (const item of items) {
                const line = String(item.summaryLine || item.SummaryLine || "");
                if (!line) {
                    continue;
                }
                rows.push({
                    app: String(app.applicationName || app.ApplicationName || "Notification"),
                    emoji: String(app.applicationEmoji || app.ApplicationEmoji || ""),
                    emojiImageUrl: String(app.applicationEmojiImageUrl || app.ApplicationEmojiImageUrl || ""),
                    template: String(tmpl.templateDisplayName || tmpl.TemplateDisplayName || ""),
                    line
                });
            }
        }
    }
    return rows;
}

function appEmojiHtml(
    row: { emoji: string; emojiImageUrl: string },
    formatAppEmoji?: InboxFormatAppEmoji
): string {
    if (!row.emoji && !row.emojiImageUrl) {
        return "";
    }
    if (typeof formatAppEmoji === "function") {
        const html = formatAppEmoji(row.emoji || "", row.emojiImageUrl);
        return html ? `<span class="msgauto-inbox__app-emoji">${html}</span>` : "";
    }
    if (row.emojiImageUrl) {
        return `<span class="msgauto-inbox__app-emoji"><img src="${escapeAttr(row.emojiImageUrl)}" alt=""></span>`;
    }
    const name = String(row.emoji || "").replace(/^:|:$/g, "");
    const glyph = EMOJI[name];
    return glyph ? `<span class="msgauto-inbox__app-emoji">${glyph}</span>` : "";
}

function slackPayload(item: Record<string, unknown>): string {
    return rowBody(item);
}

function slackSubject(item: Record<string, unknown>): string {
    return rowSubject(item);
}

function rowSentAt(item: Record<string, unknown>): unknown {
    return item.sentAt ?? item.SentAt ?? item.processedUtc ?? item.ProcessedUtc;
}

function messageBodyHtml(line: string, state: RenderState, subject?: string): string {
    const inner = typeof state.formatBody === "function" ? state.formatBody(line, subject) : formatMrkdwn(line);
    if (state.slackPreview) {
        return `<div class="msgauto-inbox__slack ma-ae-slack-html ma-ae-slack-preview-surface"><div class="ma-ae-slack-html-body ma-ae-slack-html-body--slack-surface">${inner}</div></div>`;
    }
    return `<div class="msgauto-inbox__body">${inner}</div>`;
}

function stripMrkdwn(raw: string, keepBreaks = false): string {
    let text = String(raw || "")
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/:[a-zA-Z0-9_+\-]+:/g, " ")
        .replace(/\*([^*]+)\*/g, "$1")
        .replace(/_([^_\n]+)_/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/<([^|>]+)\|([^>]+)>/g, "$2")
        .replace(/<([^>]+)>/g, "$1");
    if (keepBreaks) {
        return text
            .replace(/[^\S\n]+/g, " ")
            .replace(/ *\n */g, "\n")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
    }
    return text
        .replace(/[#>•]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function firstContainerBlock(raw: string): Record<string, unknown> | null {
    if (!isBlockKitJson(raw)) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        const blocks = Array.isArray(parsed) ? parsed : [parsed];
        for (const block of blocks) {
            if (isRecord(block) && (block.type === "container" || block.rich_text_title || block.child_blocks)) {
                return block;
            }
        }
    } catch {
        return null;
    }
    return null;
}

function formatChip(iso: unknown): string {
    if (!iso) {
        return "New";
    }
    const date = new Date(String(iso));
    if (isNaN(date.getTime())) {
        return "New";
    }
    const now = new Date();
    const diffMin = Math.round((now.getTime() - date.getTime()) / 60000);
    if (diffMin >= 0 && diffMin < 20) {
        return "Just a few minutes";
    }
    const sameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();
    if (sameDay) {
        return date.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    return date.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const HUB_ICON =
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 7.2C5 5.43 6.43 4 8.2 4h7.6C17.57 4 19 5.43 19 7.2v6.1c0 1.77-1.43 3.2-3.2 3.2H11l-4.5 3.2v-3.2H8.2C6.43 16.5 5 15.07 5 13.3V7.2Z"/></svg>`;

function hubFields(item: Record<string, unknown>): { title: string; desc: string; chip: string } {
    const chip = formatChip(rowSentAt(item));
    const subject = stripMrkdwn(slackSubject(item));
    const raw = String(item.bodyRaw || item.BodyRaw || item.slackMessage || item.SlackMessage || "").trim();
    const container = firstContainerBlock(raw);
    let title = "";
    let desc = "";
    if (container) {
        title = stripMrkdwn(richTextBlockToMrkdwn(container.rich_text_title), true);
        const children = Array.isArray(container.child_blocks) ? container.child_blocks : [];
        desc = stripMrkdwn(children.map(blockToMrkdwn).filter(Boolean).join("\n\n"), true);
    }
    if (!title || !desc) {
        const slack = slackPayload(item);
        const rows = collectRows(item);
        if (slack) {
            const plain = stripMrkdwn(slack, true);
            title = title || subject || plain;
            desc = desc || plain;
            if (title && desc.toLowerCase().startsWith(title.toLowerCase())) {
                desc = desc.slice(title.length).trim();
            }
        } else if (rows.length) {
            title = title || rows[0].template || rows[0].app;
            desc = desc || rows.map((row) => stripMrkdwn(row.line, true)).filter(Boolean).join("\n");
        }
    }
    return {
        title: title || "Notification",
        desc,
        chip
    };
}

function linkifyPlain(text: string): string {
    return escapeHtml(text).replace(
        /(https?:\/\/[^\s<]+)/g,
        `<a href="$1" target="_blank" rel="noopener">$1</a>`
    );
}

function overlayFields(title: string, desc: string): { heading: string; body: string } {
    const heading = title.trim();
    const body = desc.trim();
    const shouldUnpack = !body || heading.length > 110 || heading.includes("•");
    const source = shouldUnpack ? [heading, body].filter(Boolean).join("\n\n") : body;
    const lead =
        source.match(/^(Good (?:morning|afternoon|evening)[^!.?]*[.!?])/i) ||
        source.match(/^(.{8,90}?[.!?])/);
    if (shouldUnpack && lead) {
        return {
            heading: lead[1].trim(),
            body: source.slice(lead[1].length).trim()
        };
    }
    return { heading: heading || "Notification", body };
}

function overlayBodyHtml(desc: string): string {
    if (!desc.trim()) {
        return "";
    }
    let text = desc.replace(/\s*•\s*/g, "\n• ").replace(/\r/g, "");
    text = text.replace(/([.!?])\s+(?=[A-Z][A-Za-z].{0,48}:)/g, "$1\n\n");
    const lines = text
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    let html = "";
    const items: string[] = [];
    const flushList = (): void => {
        if (!items.length) {
            return;
        }
        html +=
            `<ul class="overlay__list">` +
            items.map((item) => `<li>${linkifyPlain(item)}</li>`).join("") +
            `</ul>`;
        items.length = 0;
    };
    lines.forEach((line, index) => {
        if (/^[-•*]/.test(line)) {
            items.push(line.replace(/^[-•*]\s*/, ""));
            return;
        }
        flushList();
        const next = lines[index + 1] || "";
        const labeled = line.match(/^([^:]{3,56}):\s*(.*)$/);
        if (labeled && !/^https?:/i.test(labeled[1])) {
            html += `<h5 class="overlay__section">${escapeHtml(labeled[1])}</h5>`;
            if (labeled[2]) {
                html += `<p>${linkifyPlain(labeled[2])}</p>`;
            }
            return;
        }
        const isHeading = next.startsWith("•") && line.length < 80 && !/[.!?]$/.test(line);
        if (isHeading) {
            html += `<h5 class="overlay__section">${escapeHtml(line)}</h5>`;
            return;
        }
        const chunks = line.length > 220 ? line.split(/(?<=\.)\s+(?=[A-Z])/) : [line];
        chunks.forEach((chunk) => {
            if (chunk.trim()) {
                html += `<p>${linkifyPlain(chunk.trim())}</p>`;
            }
        });
    });
    flushList();
    return html;
}

function closeMessageOverlay(): void {
    const overlay = document.getElementById(OVERLAY_ID);
    overlay?.classList.remove("is-open");
    overlay?.setAttribute("hidden", "");
    document.body.classList.remove("overlay-open");
}

function ensureMessageOverlay(): HTMLElement {
    let overlay = document.getElementById(OVERLAY_ID);
    if (overlay) {
        return overlay;
    }
    overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.className = "overlay";
    overlay.setAttribute("hidden", "");
    overlay.addEventListener("click", (event) => {
        const target = event.target as Element | null;
        if (target === overlay || target?.closest("[data-msgauto-overlay-close]")) {
            closeMessageOverlay();
        }
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            closeMessageOverlay();
        }
    });
    document.body.appendChild(overlay);
    return overlay;
}

function openMessageOverlay(item: Record<string, unknown>): void {
    const fields = hubFields(item);
    const { heading, body } = overlayFields(fields.title, fields.desc);
    const overlay = ensureMessageOverlay();
    overlay.innerHTML =
        `<div class="overlay__block" role="dialog" aria-modal="true" aria-labelledby="msgauto-overlay-title" tabindex="-1">` +
        `<button type="button" class="overlay__close close-btn" data-msgauto-overlay-close="1" aria-label="Close">${OVERLAY_CLOSE_SVG}</button>` +
        `<div class="overlay__content soc-announcements__details">` +
        (fields.chip ? `<p class="overlay__when"><span class="overlay__chip">${escapeHtml(fields.chip)}</span></p>` : "") +
        `<h4 id="msgauto-overlay-title">${escapeHtml(heading)}</h4>` +
        `<div class="overlay__body">${overlayBodyHtml(body)}</div>` +
        `</div></div>`;
    overlay.classList.add("is-open");
    overlay.removeAttribute("hidden");
    document.body.classList.add("overlay-open");
    (overlay.querySelector(".overlay__block") as HTMLElement | null)?.focus();
}

function bindPreviewRows(root: Element, items: Record<string, unknown>[]): void {
    root.querySelectorAll("[data-msgauto-open]").forEach((node) => {
        const index = Number.parseInt(node.getAttribute("data-msgauto-open") || "", 10);
        const item = items[index];
        if (!item) {
            return;
        }
        const open = (): void => openMessageOverlay(item);
        node.addEventListener("click", open);
        node.addEventListener("keydown", (event) => {
            const key = (event as KeyboardEvent).key;
            if (key === "Enter" || key === " ") {
                event.preventDefault();
                open();
            }
        });
    });
}

function renderHubItem(item: Record<string, unknown>, index = 0): string {
    const { title, desc, chip } = hubFields(item);
    const chipHtml = chip ? `<span class="msgauto-inbox__chip">${escapeHtml(chip)}</span>` : "";
    const descHtml = desc ? `<p class="msgauto-inbox__row-desc">${escapeHtml(desc)}</p>` : "";
    return (
        `<li class="msgauto-inbox__item msgauto-inbox__item--row msgauto-inbox__item--preview" data-msgauto-open="${index}" role="button" tabindex="0">` +
        `<span class="msgauto-inbox__icon" aria-hidden="true">${HUB_ICON}</span>` +
        `<div class="msgauto-inbox__row-body">` +
        `<p class="msgauto-inbox__row-title">${escapeHtml(title)}</p>` +
        descHtml +
        chipHtml +
        `</div>` +
        `</li>`
    );
}

function renderSlackItem(item: Record<string, unknown>, state: RenderState): string {
    const slack = slackPayload(item);
    const when = formatWhen(rowSentAt(item));
    const head = when
        ? `<div class="msgauto-inbox__item-head"><time class="msgauto-inbox__time">${escapeHtml(when)}</time></div>`
        : "";
    return `<li class="msgauto-inbox__item msgauto-inbox__item--slack">${head}${messageBodyHtml(slack, state, slackSubject(item))}</li>`;
}

function render(root: Element, state: RenderState): void {
    let shellClass = "msgauto-inbox";
    if (state.offcanvas) {
        shellClass += " msgauto-inbox--offcanvas";
    } else if (state.fillViewport) {
        shellClass += " msgauto-inbox--fill";
    }
    if (state.hideHeader) {
        shellClass += " msgauto-inbox--card";
    }

    const closeBtn = state.offcanvas
        ? `<button type="button" class="msgauto-inbox__close" data-msgauto-oc-close="1" aria-label="Close">×</button>`
        : "";
    const header = state.hideHeader
        ? ""
        : `<div class="msgauto-inbox__header">` +
          `<h2 class="msgauto-inbox__title" id="msgauto-inbox-title">AskGM Hub</h2>` +
          `<div class="msgauto-inbox__header-end">` +
          (state.meta ? `<span class="msgauto-inbox__meta">${escapeHtml(state.meta)}</span>` : "") +
          closeBtn +
          `</div></div>`;

    if (state.status) {
        const signIn = state.signIn
            ? `<button type="button" class="msgauto-inbox__signin" data-msgauto-signin="1">Sign in</button>`
            : "";
        root.innerHTML =
            `<div class="${shellClass}">${header}` +
            `<div class="msgauto-inbox__status${state.error ? " msgauto-inbox__status--error" : ""}">` +
            `<p>${escapeHtml(state.status)}</p>${signIn}</div></div>`;
        return;
    }

    const items = state.items || [];
    const list = items
        .map((item, index) => {
            if (state.hideHeader) {
                return renderHubItem(item, index);
            }
            if (slackPayload(item)) {
                return renderSlackItem(item, state);
            }

            const rows = collectRows(item);
            const when = formatWhen(rowSentAt(item));
            const body = rows.length
                ? rows
                    .map((row, index) => {
                        const appHead =
                            index === 0 || row.app !== rows[index - 1].app
                                ? `<div class="msgauto-inbox__item-head">` +
                                  `<p class="msgauto-inbox__app">${appEmojiHtml(row, state.formatAppEmoji)}${escapeHtml(row.app)}</p>` +
                                  (index === 0 && when
                                      ? `<time class="msgauto-inbox__time">${escapeHtml(when)}</time>`
                                      : "") +
                                  `</div>`
                                : "";
                        const tmpl =
                            row.template && row.template !== row.app
                                ? `<p class="msgauto-inbox__template">${escapeHtml(row.template)}</p>`
                                : "";
                        return appHead + tmpl + messageBodyHtml(row.line, state);
                    })
                    .join("")
                : `<div class="msgauto-inbox__item-head"><p class="msgauto-inbox__app">Notification</p>` +
                  (when ? `<time class="msgauto-inbox__time">${escapeHtml(when)}</time>` : "") +
                  `</div>`;
            return `<li class="msgauto-inbox__item">${body}</li>`;
        })
        .join("");

    let pager = "";
    if (state.pager && (state.hasPrevious || state.hasNext)) {
        const prevDisabled = !state.hasPrevious;
        const nextDisabled = !state.hasNext;
        pager =
            `<div class="msgauto-inbox__pager">` +
            `<button type="button" data-msgauto-page="prev"${prevDisabled ? " disabled" : ""}>Previous</button>` +
            `<span class="msgauto-inbox__page">Page ${escapeHtml(state.page || 1)}</span>` +
            `<button type="button" data-msgauto-page="next"${nextDisabled ? " disabled" : ""}>Next</button>` +
            `</div>`;
    }

    root.innerHTML = `<div class="${shellClass}">${header}<ul class="msgauto-inbox__list">${list}</ul>${pager}</div>`;
}

function pagePayload(data: Record<string, unknown>): Record<string, unknown> {
    const inner = data.result ?? data.Result;
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        return inner as Record<string, unknown>;
    }
    return data;
}

function notificationsFromPage(page: Record<string, unknown>): Record<string, unknown>[] {
    const payload = pagePayload(page);
    const raw =
        payload.rows ??
        payload.Rows ??
        payload.notifications ??
        payload.Notifications ??
        payload.items ??
        payload.Items;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.filter(isRecord);
}

function nextCursorFromPage(page: Record<string, unknown>): string | null {
    const payload = pagePayload(page);
    const value = payload.nextCursor != null ? payload.nextCursor : payload.NextCursor;
    if (value == null || value === "") {
        return null;
    }
    return String(value);
}

function isInteractionRequired(err: unknown): boolean {
    return !!err && typeof err === "object" && (err as { name?: string }).name === "InteractionRequiredAuthError";
}

function ensureOffcanvasShell(): HTMLElement {
    let shell = document.getElementById("msgauto-inbox-offcanvas");
    if (shell) {
        return shell;
    }

    shell = document.createElement("div");
    shell.id = "msgauto-inbox-offcanvas";
    shell.className = "msgauto-inbox-oc";
    shell.innerHTML =
        `<div class="msgauto-inbox-oc__backdrop" data-msgauto-oc-close="1"></div>` +
        `<aside class="msgauto-inbox-oc__panel" role="dialog" aria-modal="true" aria-labelledby="msgauto-inbox-title">` +
        `<div class="msgauto-inbox-oc__mount"></div>` +
        `</aside>`;
    document.body.appendChild(shell);
    return shell;
}

export function mount(target: string | Element, options: MsgAutoInboxMountOptions): MsgAutoInboxHandle {
    const opts = options || ({} as MsgAutoInboxMountOptions);
    const trigger = resolveRoot(target);
    if (!trigger) {
        throw new Error("MsgAutoInbox.mount: target element not found.");
    }

    const apiBase = (opts.apiBase || "").replace(/\/$/, "");
    const getToken = opts.getToken;
    const offcanvas = !!opts.offcanvas;
    const fillViewport = !!opts.fillViewport && !offcanvas;
    const pageSize = opts.pageSize || 20;
    const lazy = !!opts.lazy && offcanvas;
    const cursorStack: Array<string | null> = [null];
    let pageIndex = 0;
    let loadedOnce = false;
    let loadSeq = 0;
    let lastFingerprint = "";
    const pollMs = opts.pollMs && opts.pollMs > 0 ? opts.pollMs : 0;

    if (!apiBase) {
        throw new Error("MsgAutoInbox.mount: apiBase is required.");
    }
    if (opts.authMode !== "cookie" && typeof getToken !== "function") {
        throw new Error("MsgAutoInbox.mount: getToken() is required.");
    }

    injectInboxStyles();

    let root: Element = trigger;
    let shell: HTMLElement | null = null;
    if (offcanvas) {
        shell = ensureOffcanvasShell();
        root = shell.querySelector(".msgauto-inbox-oc__mount") as Element;
        const openPanel = (): void => {
            shell!.classList.add("is-open");
            document.body.classList.add("msgauto-inbox-oc-open");
            if (lazy && !loadedOnce) {
                load(true);
            }
        };
        const closePanel = (): void => {
            shell!.classList.remove("is-open");
            document.body.classList.remove("msgauto-inbox-oc-open");
        };
        trigger.addEventListener("click", (event) => {
            event.preventDefault();
            openPanel();
        });
        shell.addEventListener("click", (event) => {
            const close = (event.target as Element | null)?.closest("[data-msgauto-oc-close]");
            if (close) {
                closePanel();
            }
        });
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                closePanel();
            }
        });
    }

    function renderState(state: RenderState): void {
        state.offcanvas = offcanvas;
        state.fillViewport = fillViewport;
        state.slackPreview = !!opts.slackPreview;
        state.hideHeader = !!opts.hideHeader;
        state.pager = !!opts.pager;
        state.pageSize = pageSize;
        state.formatBody = opts.formatBody;
        state.formatAppEmoji = opts.formatAppEmoji;
        render(root, state);
    }

    function inboxUrl(path: string, params?: URLSearchParams): string {
        const suffix = params && [...params.keys()].length ? `?${params.toString()}` : "";
        if (opts.authMode === "cookie") {
            return `${apiBase}/Inbox${path}${suffix}`;
        }
        return `${apiBase}/v2.0/Notifications${path}${suffix}`;
    }

    function load(interactive = true, silent = false): void {
        const seq = ++loadSeq;
        loadedOnce = true;
        if (!silent) {
            renderState({ status: "Loading messages…" });
        }
        const cookie = opts.authMode === "cookie";
        const tokenP = Promise.resolve(typeof getToken === "function" ? getToken(interactive) : "");
        tokenP
            .then((token) => {
                if (!cookie && !token) {
                    throw new Error("getToken() returned no access token.");
                }
                const params = new URLSearchParams();
                params.set("pageSize", String(pageSize));
                const cursor = cursorStack[pageIndex];
                if (cursor) {
                    params.set("cursor", cursor);
                }
                const headers: Record<string, string> = { Accept: "application/json" };
                if (!cookie) {
                    headers.Authorization = "Bearer " + token;
                }
                return fetch(inboxUrl("", params), {
                    headers,
                    credentials: cookie ? "include" : "omit"
                });
            })
            .then((response) => {
                if (response.status === 401 || response.status === 403) {
                    if (cookie) {
                        const err = new Error("interaction_required");
                        err.name = "InteractionRequiredAuthError";
                        throw err;
                    }
                    throw new Error("Not authorized. Token must be for the MsgAuto API.");
                }
                if (!response.ok) {
                    throw new Error(`Notifications request failed (${response.status}).`);
                }
                return response.json();
            })
            .then((data: Record<string, unknown>) => {
                if (seq !== loadSeq) {
                    return;
                }
                if (silent && pageIndex !== 0) {
                    return;
                }
                const items = notificationsFromPage(data);
                const nextCursor = nextCursorFromPage(data);
                const fingerprint =
                    items
                        .map((item) => String(item.messageId || item.MessageId || item.recordKey || item.RecordKey || ""))
                        .join("|") + `:${nextCursor || ""}`;
                if (silent && fingerprint === lastFingerprint) {
                    return;
                }
                lastFingerprint = fingerprint;
                const total = (data.totalCount != null ? data.totalCount : data.TotalCount) as number | undefined;
                if (!items.length && pageIndex === 0) {
                    renderState({ status: "No messages yet.", meta: "0" });
                    return;
                }
                const count = typeof total === "number" ? total : items.length;
                renderState({
                    items,
                    page: pageIndex + 1,
                    hasPrevious: pageIndex > 0,
                    hasNext: !!nextCursor,
                    meta: `${count} message${count === 1 ? "" : "s"}`
                });
                if (opts.hideHeader) {
                    bindPreviewRows(root, items);
                }
                root.querySelectorAll("[data-msgauto-page]").forEach((button) => {
                    button.addEventListener("click", () => {
                        if (button.getAttribute("data-msgauto-page") === "next") {
                            if (!nextCursor) {
                                return;
                            }
                            cursorStack.splice(pageIndex + 1);
                            cursorStack.push(nextCursor);
                            pageIndex += 1;
                        } else {
                            pageIndex = Math.max(0, pageIndex - 1);
                        }
                        load(true);
                    });
                });
            })
            .catch((err: Error) => {
                if (seq !== loadSeq) {
                    return;
                }
                if (silent) {
                    return;
                }
                if (isInteractionRequired(err)) {
                    renderState({ status: "Sign in to see your messages.", signIn: true });
                    root.querySelector("[data-msgauto-signin]")?.addEventListener("click", () => {
                        if (cookie) {
                            window.open(apiBase, "_blank", "noopener");
                        }
                        load(true);
                    });
                    return;
                }
                renderState({ status: err.message || String(err), error: true });
            });
    }

    if (opts.authPrompt) {
        load(false);
    } else if (!lazy) {
        load(true);
    } else {
        renderState({ status: "Open to load messages." });
    }

    if (pollMs) {
        const poll = (): void => {
            if (document.hidden || !loadedOnce || pageIndex !== 0) {
                return;
            }
            load(false, true);
        };
        window.setInterval(poll, pollMs);
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) {
                poll();
            }
        });
    }

    return { reload: () => load(true) };
}

declare global {
    interface Window {
        MsgAutoInbox?: { mount: typeof mount };
    }
}

if (typeof window !== "undefined") {
    window.MsgAutoInbox = { mount };
}
